import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../atoms/Button';
import { PageHeader } from '../molecules/PageHeader';
import { loadPayoutsFromStorage } from '@/utils/storage';
import { calculateForm2086, type Form2086Entry } from '@/utils/cgi150vhbis';
import { exportForm2086 } from '@/utils/form2086';
import { formatCurrency, formatDate } from '@/utils/date';

const PARAMS_KEY = 'cryptotrack_2086_params';

interface Form2086Params {
  acquisitionTotal: number;
  capitalRecovered: number;
}

const loadParams = (): Form2086Params => {
  try {
    const stored = localStorage.getItem(PARAMS_KEY);
    if (stored) return JSON.parse(stored) as Form2086Params;
  } catch { /* ignore */ }
  return { acquisitionTotal: 0, capitalRecovered: 0 };
};

const saveParams = (params: Form2086Params): void => {
  try {
    localStorage.setItem(PARAMS_KEY, JSON.stringify(params));
  } catch { /* ignore */ }
};

export const PlusValuesPage: React.FC = () => {
  const [entries, setEntries] = useState<Form2086Entry[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);
  const [paramsInput, setParamsInput] = useState<Form2086Params>({ acquisitionTotal: 0, capitalRecovered: 0 });
  const [showParams, setShowParams] = useState(false);
  const [paramsSaved, setParamsSaved] = useState(false);

  const recompute = useCallback(
    async (p: Form2086Params) => {
      const txs = await loadPayoutsFromStorage();
      const result = calculateForm2086(txs, p.acquisitionTotal, p.capitalRecovered);
      setEntries(result);
    },
    []
  );

  useEffect(() => {
    const init = async () => {
      try {
        const stored = loadParams();
        setParamsInput(stored);
        await recompute(stored);
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [recompute]);

  const handleSaveParams = async () => {
    saveParams(paramsInput);
    setIsLoading(true);
    try {
      await recompute(paramsInput);
    } finally {
      setIsLoading(false);
    }
    setParamsSaved(true);
    setTimeout(() => setParamsSaved(false), 2000);
  };

  // Filtrage par année
  const yearlyEntries = entries.filter(
    (e) => new Date(e.case211).getFullYear() === selectedYear
  );

  const availableYears = Array.from(
    new Set(entries.map((e) => new Date(e.case211).getFullYear()))
  ).sort((a, b) => b - a);

  const hasMissingPortfolioValue = yearlyEntries.some((e) => e.missingPortfolioValue);

  const totalSales = yearlyEntries.reduce((sum, e) => sum + e.case217, 0);
  const totalGain = yearlyEntries.reduce((sum, e) => sum + e.case224, 0);
  const totalTax = totalGain > 0 ? totalGain * 0.314 : 0;
  // Franchise CGI art. 150 VH bis : sous ce seuil de cessions annuelles, aucun impôt n'est dû
  const isExemptFromTax = totalSales < 305;

  // Résumé 220/221 final pour l'année (dernière cession de l'année, ou global)
  const lastEntry = yearlyEntries[yearlyEntries.length - 1];

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 pt-8 pb-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-8">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Plus-values et moins-values"
          subtitle="Méthode légale française — CGI art. 150 VH bis (formulaire 2086)"
        />

        {/* Paramètres initiaux (accordéon) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 border border-blue-100">
          <button
            type="button"
            onClick={() => setShowParams((v) => !v)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="font-semibold text-gray-800 dark:text-gray-200">Paramètres initiaux (cases 220 / 221 antérieures à l&apos;app)</span>
            </div>
            <svg
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${showParams ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showParams && (
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-gray-800">
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
                Si vous aviez des acquisitions de cryptos ou des cessions <strong>avant de commencer à utiliser cette application</strong>,
                renseignez ici les totaux cumulés. Ces valeurs s&apos;ajoutent aux données enregistrées dans l&apos;app.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Case 220 initiale — Prix total d&apos;acquisition antérieur (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paramsInput.acquisitionTotal}
                    onChange={(e) => setParamsInput((p) => ({ ...p, acquisitionTotal: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Case 221 initiale — Capital déjà récupéré (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paramsInput.capitalRecovered}
                    onChange={(e) => setParamsInput((p) => ({ ...p, capitalRecovered: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Button onClick={handleSaveParams} className="bg-blue-600 hover:bg-blue-700">
                  Appliquer
                </Button>
                {paramsSaved && (
                  <span className="text-sm text-green-600 font-medium">Sauvegardé ✓</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sélecteur d'année + export */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Année fiscale</label>
              {availableYears.length > 0 ? (
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="ml-4 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              ) : (
                <p className="ml-4 text-gray-500 dark:text-gray-400">Aucune cession enregistrée</p>
              )}
            </div>

            <Button
              onClick={() => exportForm2086(yearlyEntries, selectedYear)}
              disabled={yearlyEntries.length === 0}
              className="px-6 py-3 bg-green-600 hover:bg-green-700"
            >
              <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Exporter formulaire 2086
            </Button>
          </div>
        </div>

        {/* Avertissement case 212 manquante */}
        {hasMissingPortfolioValue && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="font-semibold text-amber-900">Valeur portefeuille (case 212) manquante</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Certaines cessions n&apos;ont pas de valeur de portefeuille renseignée (case 212). La plus-value ne peut pas être calculée pour ces lignes.
                  Modifiez les transactions concernées pour y ajouter la valeur totale de votre portefeuille en € au moment de la vente.
                </p>
              </div>
            </div>
          </div>
        )}

        {yearlyEntries.length > 0 ? (
          <>
            {/* Cartes de résumé */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Prix de cession net total (217)</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(totalSales)}</div>
              </div>

              <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 ${totalGain >= 0 ? 'border-l-green-500' : 'border-l-red-500'}`}>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Plus-value nette totale (224)</div>
                <div className={`text-2xl font-bold ${totalGain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
                </div>
              </div>

              {isExemptFromTax ? (
                <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg shadow p-6 border-l-4 border-l-blue-500">
                  <div className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">Exonéré (seuil 305 €)</div>
                  </div>
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    Cessions sous le seuil annuel de 305 € : aucun impôt dû (CGI art. 150 VH bis).
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-l-orange-500">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Impôt estimé (31.4%)</div>
                  <div className="text-2xl font-bold text-orange-600">{formatCurrency(totalTax)}</div>
                </div>
              )}
            </div>

            {/* Tableau détaillé des cessions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Détail des cessions — Formulaire 2086</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Article 150 VH bis du CGI — une ligne par cession</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm text-gray-900 dark:text-gray-100">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">211 — Date</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Actif cédé</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Quantité</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-950/40">212 — Portefeuille</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">213 — Cession brute</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">214 — Frais</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400">217 — Cession nette</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-950/40">220 — Acq. totale</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-950/40">221 — Capital récup.</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-950/40">223 — Acq. nette</th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 bg-green-50 dark:bg-green-950/40">224 — Plus-value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {yearlyEntries.map((e) => (
                      <tr key={e.disposalId} className="hover:bg-gray-50 hover:dark:bg-gray-900">
                        <td className="px-3 py-3">{formatDate(e.case211)}</td>
                        <td className="px-3 py-3">
                          <span className="font-medium">{e.crypto.name}</span>
                          <span className="text-gray-400 dark:text-gray-500 ml-1">({e.crypto.symbol})</span>
                        </td>
                        <td className="px-3 py-3 text-right font-mono">{e.amountSold.toFixed(8)}</td>
                        <td className="px-3 py-3 text-right bg-blue-50 dark:bg-blue-950/40">
                          {e.missingPortfolioValue ? (
                            <span className="text-amber-600 font-semibold">— manquant</span>
                          ) : (
                            formatCurrency(e.case212)
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">{formatCurrency(e.case213)}</td>
                        <td className="px-3 py-3 text-right">{formatCurrency(e.case214)}</td>
                        <td className="px-3 py-3 text-right font-medium">{formatCurrency(e.case217)}</td>
                        <td className="px-3 py-3 text-right bg-yellow-50 dark:bg-yellow-950/40">{formatCurrency(e.case220)}</td>
                        <td className="px-3 py-3 text-right bg-yellow-50 dark:bg-yellow-950/40">{formatCurrency(e.case221)}</td>
                        <td className="px-3 py-3 text-right bg-yellow-50 dark:bg-yellow-950/40 font-medium">{formatCurrency(e.case223)}</td>
                        <td className={`px-3 py-3 text-right bg-green-50 dark:bg-green-950/40 font-bold ${e.case224 >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {e.missingPortfolioValue ? (
                            <span className="text-amber-600">—</span>
                          ) : (
                            <>{e.case224 >= 0 ? '+' : ''}{formatCurrency(e.case224)}</>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 dark:bg-gray-900 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                    <tr>
                      <td className="px-3 py-3" colSpan={6}>TOTAL</td>
                      <td className="px-3 py-3 text-right">{formatCurrency(totalSales)}</td>
                      <td className="px-3 py-3 bg-yellow-50 dark:bg-yellow-950/40" colSpan={2}></td>
                      <td className="px-3 py-3 bg-yellow-50 dark:bg-yellow-950/40"></td>
                      <td className={`px-3 py-3 text-right bg-green-50 dark:bg-green-950/40 ${totalGain >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Récapitulatif fiscal */}
            {lastEntry && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Récapitulatif fiscal {selectedYear}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600 dark:text-gray-400">Case 220 — Prix total d&apos;acquisition du portefeuille</span>
                      <span className="font-medium">{formatCurrency(lastEntry.case220)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600 dark:text-gray-400">Case 221 — Capital récupéré (après cessions {selectedYear})</span>
                      <span className="font-medium">
                        {formatCurrency(
                          lastEntry.case221 + (lastEntry.missingPortfolioValue ? 0 : (lastEntry.case223 * lastEntry.case217) / (lastEntry.case212 || 1))
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 dark:text-gray-400">Plus-values imposables</span>
                      <span className={`font-bold ${totalGain >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
                      </span>
                    </div>
                  </div>
                  {isExemptFromTax ? (
                    <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg p-4 border border-blue-200 dark:border-blue-900">
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">Exonéré — seuil de 305 €</p>
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        Le prix de cession net total ({formatCurrency(totalSales)}) est inférieur au seuil annuel de 305 € :
                        aucun impôt n&apos;est dû sur ces cessions, quel que soit le résultat (CGI art. 150 VH bis).
                      </p>
                    </div>
                  ) : (
                    <div className="bg-orange-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Impôt forfaitaire estimé (Flat Tax 31.4%)</p>
                      <p className="text-3xl font-bold text-orange-600">{formatCurrency(totalTax)}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Taux applicable depuis janvier 2026</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Aucune cession enregistrée pour {selectedYear}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Les ventes et swaps apparaîtront ici une fois enregistrés
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
